import React from "react";
import { InspectionRequestPreferenceMeridiem, AM, PM } from "../utils/InspectionRequestPreferenceMeridiem";
import useInspection from "../../../../hooks/inspections/useInspection";
import InspectionTypeScheduleCalendarDayButtonStyles from "../utils/InspectoinTypeScheduleCalendarDayButtonStyles";
import InspectionTypeScheduleCalendarDayCount from "../utils/InspectionTypeScheduleCalendarDayCount";
import useInspectionType from "../../../../../tanstack/inspection-types/useInspectionType";
import { CalendarDate, parseDate, isEqualDay, isEqualMonth, isEqualYear, getDayOfWeek } from "@internationalized/date";
import { BlockOutDay, BlockOutDayDoWToJSDateDoW } from "../utils/InspectionTypeBlockOutDay";
import { InspectionTypeMeridiemType } from "@gogovapps/core-service-client/types/inputTypeSchemas/InspectionTypeMeridiemSchema";
import useInspectionSchedule from "../../../../hooks/inspections/useInspectionSchedule";

interface IInspectionTypeScheduleCalendarDay {
  inspectionTypeId: string;
  inspectorId: string;
  inspectionId: string | undefined;
  date: string;
  selectedDate: CalendarDate | null;
  selectedInspectorId: string | null;
  selectedMeridiem: InspectionRequestPreferenceMeridiem | null;
  onClick: (inspectorId: string, day: string, meridiem: InspectionRequestPreferenceMeridiem) => void;
}
export default function InspectionTypeScheduleCalendarDay({
  inspectionTypeId,
  inspectorId,
  inspectionId,
  date,
  selectedDate,
  selectedMeridiem,
  selectedInspectorId,
  onClick,
}: IInspectionTypeScheduleCalendarDay) {
  const scheduleQuery = useInspectionSchedule(inspectionId ?? "");
  const { inspectionQuery } = useInspection({ id: inspectionId ?? "", enabled: !!inspectionId });
  const scheduledDate = inspectionQuery.data?.data.attributes["scheduled-date"];
  const scheduledMeridiem = inspectionQuery.data?.data.attributes["scheduled-meridiem"] as InspectionTypeMeridiemType;

  const assigneeId = (inspectionQuery.data?.data.attributes as any)?.["assignee-id"];

  const scheduledCalendarDate = scheduledDate ? parseDate(scheduledDate) : null;

  const { data } = useInspectionType({ id: inspectionTypeId, enabled: true });
  const blockOutDays = data?.data.attributes.blockOutDays ?? [];

  const inspectorSchedule = scheduleQuery.data?.schedule[inspectorId] ?? {};
  const inspectorDateSchedule = inspectorSchedule[date] ?? {};
  const amInspectionTotal = inspectorDateSchedule["AM"] ?? 0;
  const pmInspectionTotal = inspectorDateSchedule["PM"] ?? 0;
  const currentInspectionDate = parseDate(date);

  const dateYearMonthDayEqual = (date: CalendarDate, other: CalendarDate) => {
    return isEqualDay(date, other) && isEqualMonth(date, other) && isEqualYear(date, other);
  };

  const requestPreferenceDate = currentInspectionDate;
  const isInspectorSelected = inspectorId == selectedInspectorId;
  const isDateSelected = selectedDate !== null && dateYearMonthDayEqual(requestPreferenceDate, selectedDate);
  const isDateAndInspectorSelected = isInspectorSelected && isDateSelected;
  const amBlockedOut = isAMBlockedOut(requestPreferenceDate, blockOutDays);
  const pmBlockedOut = isPMBlockedOut(requestPreferenceDate, blockOutDays);

  const selectedAM = isDateAndInspectorSelected && selectedMeridiem === AM;
  const selectedPM = isDateAndInspectorSelected && selectedMeridiem === PM;

  const amCount = new InspectionTypeScheduleCalendarDayCount({
    selectedInspectorId: selectedInspectorId,
    currentInspectorId: inspectorId,
    currentCalendarDateCount: amInspectionTotal,
    currentCalendarDate: currentInspectionDate,
    currentCalendarMeridiem: "AM",
    inspectionScheduledAtDate: scheduledCalendarDate,
    selectedCalendarDate: selectedDate,
    selectedCalendarMeridiem: selectedMeridiem,
    inspectionScheduledAssigneeId: assigneeId,
    inspectionScheduledMeridiem: scheduledMeridiem,
  }).getCount();

  const pmCount = new InspectionTypeScheduleCalendarDayCount({
    selectedInspectorId: selectedInspectorId,
    currentInspectorId: inspectorId,
    currentCalendarDateCount: pmInspectionTotal,
    currentCalendarDate: currentInspectionDate,
    currentCalendarMeridiem: "PM",
    inspectionScheduledAtDate: scheduledCalendarDate,
    selectedCalendarDate: selectedDate,
    selectedCalendarMeridiem: selectedMeridiem,
    inspectionScheduledAssigneeId: assigneeId,
    inspectionScheduledMeridiem: scheduledMeridiem,
  }).getCount();

  const handleClick = (meridiem: InspectionRequestPreferenceMeridiem) => {
    onClick(inspectorId, date, meridiem);
  };

  const handleAMClick = () => {
    handleClick(AM);
  };

  const handlePMClick = () => {
    handleClick(PM);
  };

  return (
    <div className='tw-grid tw-grid-cols-2 tw-text-center'>
      <div className='tw-px-0'>
        <InspectionTypeScheduleCalendarDayButton
          blockedOut={amBlockedOut}
          selected={selectedAM}
          count={amCount}
          onClick={handleAMClick}
        />
      </div>
      <div className='tw-px-0'>
        <InspectionTypeScheduleCalendarDayButton
          blockedOut={pmBlockedOut}
          selected={selectedPM}
          count={pmCount}
          onClick={handlePMClick}
        />
      </div>
    </div>
  );
}

function isPMBlockedOut(date: CalendarDate, blockOutDays: BlockOutDay[]): boolean {
  return blockOutDays.some((day) => {
    const isPMBlockedOut = day.meridiem === "PM" || day.meridiem === null;
    return BlockOutDayDoWToJSDateDoW[day.dayOfWeek] === getDayOfWeek(date, "en-US") && isPMBlockedOut;
  });
}

function isAMBlockedOut(date: CalendarDate, blockOutDays: BlockOutDay[]): boolean {
  return blockOutDays.some((day) => {
    const isPMBlockedOut = day.meridiem === "AM" || day.meridiem === null;
    return BlockOutDayDoWToJSDateDoW[day.dayOfWeek] === getDayOfWeek(date, "en-US") && isPMBlockedOut;
  });
}

interface InspectionTypeScheduleCalendarDayButtonProps {
  onClick: () => void;
  selected: boolean;
  count: number;
  blockedOut: boolean;
}
function InspectionTypeScheduleCalendarDayButton({
  selected,
  count,
  onClick,
  blockedOut,
}: InspectionTypeScheduleCalendarDayButtonProps) {
  const invisible = "tw-invisible";
  const styles = new InspectionTypeScheduleCalendarDayButtonStyles(selected, blockedOut);
  return (
    <button className={styles.toString()} onClick={onClick}>
      <div className={`${count > 0 ? "" : invisible} tw-p-1`}>{count}</div>
    </button>
  );
}
